/**
 * EmailDigestSettings.tsx — Feature 22: Email Digest
 * Allows users to configure how often they receive an email digest
 * of their watched tasks, mentions, and due-soon reminders.
 */
import React, { useEffect, useState } from 'react';
import { Mail, BellOff, Calendar, MessageSquare, Clock, Bell, Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { apiClient } from '../packages/api-client';

type Frequency = 'none' | 'daily' | 'weekly';

interface DigestPrefs {
    frequency: Frequency;
    mention_alerts: boolean;
    due_soon_alerts: boolean;
    watched_task_updates: boolean;
}

const FREQ_OPTIONS: { value: Frequency; label: string; desc: string; icon: LucideIcon }[] = [
    { value: 'none', label: 'Off', desc: 'No email digests', icon: BellOff },
    { value: 'daily', label: 'Daily', desc: 'Sent each morning', icon: Mail },
    { value: 'weekly', label: 'Weekly', desc: 'Sent every Monday', icon: Calendar },
];

export function EmailDigestSettings() {
    const [prefs, setPrefs] = useState<DigestPrefs>({ frequency: 'daily', mention_alerts: true, due_soon_alerts: true, watched_task_updates: true });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        apiClient.get<DigestPrefs>('/users/me/digest-prefs')
            .then(r => setPrefs(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    async function handleSave() {
        setSaving(true); setSaved(false);
        try {
            await apiClient.patch('/users/me/digest-prefs', prefs);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch { /* show toast in prod */ }
        finally { setSaving(false); }
    }

    if (loading) return <div className="spinner" />;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 700 }}>
                    <Mail size={18} aria-hidden="true" /> Email Digest
                </h3>
                <p style={{ marginTop: 4, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                    Choose when to receive a summary email of your tasks and activity.
                </p>
            </div>

            {/* Frequency selector */}
            <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>Digest frequency</div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    {FREQ_OPTIONS.map(opt => (
                        <button key={opt.value}
                            onClick={() => setPrefs(p => ({ ...p, frequency: opt.value }))}
                            style={{ flex: 1, padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: `2px solid ${prefs.frequency === opt.value ? 'var(--color-primary)' : 'var(--color-border)'}`, background: prefs.frequency === opt.value ? 'var(--color-primary)10' : 'var(--color-surface-2)', cursor: 'pointer', textAlign: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-1)', fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>
                                <opt.icon size={16} aria-hidden="true" /> {opt.label}
                            </div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{opt.desc}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Toggles */}
            {prefs.frequency !== 'none' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: -8 }}>Include in digest</div>
                    {([
                        { key: 'mention_alerts', label: '@Mention alerts', icon: MessageSquare },
                        { key: 'due_soon_alerts', label: 'Tasks due in 24h', icon: Clock },
                        { key: 'watched_task_updates', label: 'Watched task updates', icon: Bell },
                    ] as { key: keyof DigestPrefs; label: string; icon: LucideIcon }[]).map(item => (
                        <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer', padding: 'var(--space-2) var(--space-3)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)' }}>
                            <item.icon size={18} aria-hidden="true" style={{ flexShrink: 0 }} />
                            <span style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>{item.label}</span>
                            <input type="checkbox" checked={prefs[item.key] as boolean}
                                onChange={e => setPrefs(p => ({ ...p, [item.key]: e.target.checked }))} />
                        </label>
                    ))}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save preferences'}
                </button>
                {saved && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-sm)', color: 'var(--color-success)' }}><Check size={16} aria-hidden="true" /> Saved!</span>}
            </div>
        </div>
    );
}
